import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ScanLine, LayoutGrid, Waves, ArrowRight, ArrowUpRight } from "lucide-react";
import Layout from "../components/Layout";
import Marquee from "../components/Marquee";
import ChargeRing from "../components/ChargeRing";
import { Reveal, MaskLine } from "../components/Reveal";
import { Eyebrow } from "../components/bits";
import DayOnBatteryChart from "../components/poc/DayOnBatteryChart";
import AppPreview from "../components/AppPreview";
import VenueMap from "../components/poc/VenueMap";
import { IMG, PRODUCTS } from "../data/content";

const STEPS = [
  { n: "01", icon: ScanLine, title: "Enroll", text: "Scan the serial on any device. It joins your account in seconds." },
  { n: "02", icon: LayoutGrid, title: "Set it down", text: "Any ChargeRevo surface fast-charges it — through the desk, no pad in sight." },
  { n: "03", icon: Waves, title: "Forget charging", text: "Ambient top-up keeps it alive everywhere else. Weeks pass before you think about a battery." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <Layout>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden bg-navy">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={IMG.heroHome} alt="ChargeRevo home office charging through the desk" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-navy" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-transparent" />

        <div className="absolute right-[6%] top-1/2 hidden -translate-y-1/2 lg:block">
          <div className="relative">
            <div className="absolute -inset-8 -z-10 flex items-center justify-center opacity-70" aria-hidden>
              <ChargeRing size={520} opacity={0.6} />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="w-[440px] overflow-hidden rounded-[1.75rem] border border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
              data-testid="hero-teaser-video"
            >
              <video
                src="/videos/fill2.mp4"
                poster="/videos/fill2-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="aspect-video w-full object-cover"
              />
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-revo-green/40 bg-revo-green/10 px-4 py-1.5 text-xs font-semibold text-revo-green">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-revo-green" /> Power is now a subscription
            </span>
          </motion.div>
          <h1 className="font-display text-5xl font-black leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskLine delay={0.15}>NEVER</MaskLine>
            <MaskLine delay={0.28}>PLUG IN</MaskLine>
            <MaskLine delay={0.41}><span className="text-revo-green">AGAIN.</span></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-xl text-lg leading-relaxed text-revo-ice md:text-xl">
            ChargeRevo turns every desk, counter, and room into your charger. Set your laptop down — it charges. Walk around — it tops up. One subscription covers every device you own.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-5">
            <Link to="/join" data-testid="hero-join-btn" className="group flex items-center gap-2 rounded-full bg-revo-green px-7 py-4 font-bold text-navy transition-transform duration-200 hover:scale-[1.04] hover:shadow-[0_0_40px_rgba(47,213,118,0.5)]">
              Join the waitlist <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/how-it-works" data-testid="hero-how-link" className="flex items-center gap-2 font-semibold text-white transition-colors hover:text-revo-green">
              See how it works <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 30-SECOND EXPLAINER */}
      <section className="bg-white px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><Eyebrow dark>The 30-second version</Eyebrow></Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="group h-full rounded-3xl border border-navy/10 bg-revo-surface p-8 transition-colors duration-300 hover:border-revo-green">
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-revo-green transition-colors group-hover:bg-revo-green group-hover:text-navy">
                      <s.icon className="h-6 w-6" />
                    </span>
                    <span className="font-display text-4xl font-black text-navy/10">{s.n}</span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-navy/60">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* POC 1 */}
      <section className="bg-navy px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-6xl">
          <Reveal><DayOnBatteryChart /></Reveal>
        </div>
      </section>

      {/* LIFESTYLE BAND */}
      <section className="relative overflow-hidden bg-navy">
        <div className="relative h-[60vh] min-h-[420px] w-full">
          <img src={IMG.homeFamily} alt="A family evening with every device charging wirelessly" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-5 pb-12 md:px-8">
            <Reveal>
              <h2 className="max-w-2xl font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl">
                From the couch to the kitchen, nothing needs a cable.
              </h2>
              <p className="mt-4 max-w-xl text-lg text-revo-ice">Laptops, phones, the stick vacuum, even the sensors on the wall — all enrolled to one account, all quietly topping up.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECEIVER LINEUP */}
      <section className="bg-revo-surface px-5 py-24 md:px-8" data-testid="home-receiver-lineup">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow dark>Three ways to enroll a device</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-black leading-tight tracking-tight text-navy sm:text-4xl">
              Pick a receiver. Then just set it down.
            </h2>
            <p className="mt-4 max-w-2xl text-navy/60">Every device joins your account through one small receiver — removable, portable, or permanent. Choose how it fits your life.</p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {[
              { id: "base", name: "Revo Base", img: IMG.baseHero, line: "Slides under any laptop — move it between machines anytime." },
              { id: "key", name: "Revo Key", img: IMG.keyHero, line: "A pocket dongle for any USB-C port. Fastest way to enroll." },
              { id: "tag", name: "Revo Tag", img: IMG.revoTagInstalled, line: "Install once, forever — bonds flush and never comes off." },
            ].map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <Link to={`/products#${r.id}`} data-testid={`home-receiver-${r.id}`} className="group block h-full overflow-hidden rounded-3xl border border-navy/10 bg-white transition-colors duration-300 hover:border-revo-green">
                  <div className="aspect-square overflow-hidden">
                    <img src={r.img} alt={r.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl font-extrabold text-navy">{r.name}</h3>
                      <ArrowUpRight className="h-5 w-5 text-navy/30 transition-colors group-hover:text-revo-green" />
                    </div>
                    <p className="mt-2 text-sm text-navy/60">{r.line}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <Link to="/products#receiver-comparison" className="mt-8 inline-flex items-center gap-2 font-semibold text-navy transition-colors hover:text-revo-green" data-testid="home-compare-receivers">
              Compare all three receivers <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT FAMILY */}
      <section className="bg-white px-5 py-24 md:px-8 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow dark>One account. A whole family of power.</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight text-navy sm:text-5xl">
              Surfaces, rooms, receivers, and a kitchen that runs with no batteries at all.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <Link to="/products" data-testid={`home-product-${p.id}`} className="group block h-full overflow-hidden rounded-3xl border border-navy/10 bg-revo-surface transition-colors duration-300 hover:border-revo-green">
                  <div className="aspect-[4/3] overflow-hidden bg-revo-surface">
                    <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl font-extrabold text-navy">{p.name}</h3>
                      <ArrowUpRight className="h-5 w-5 text-navy/30 transition-colors group-hover:text-revo-green" />
                    </div>
                    <p className="mt-1 text-sm font-medium text-revo-green">{p.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APP PREVIEW */}
      <AppPreview />

      {/* VENUE MAP / ROAMING */}
      <VenueMap />

      {/* CREDIBILITY */}
      <section className="bg-navy px-5 py-24 md:px-8 md:py-32 grain relative">
        <div className="mx-auto max-w-5xl text-center">
          <Reveal>
            <Eyebrow>Built on certified standards, not promises.</Eyebrow>
            <p className="mx-auto mt-6 max-w-3xl text-2xl font-medium leading-snug text-white md:text-3xl">
              ChargeRevo runs on <span className="text-revo-green">Qi2</span> — the standard inside a billion devices — and <span className="text-revo-green">Ki</span>, the new 2,200-watt cordless kitchen standard. Patent-pending cloud entitlement, metering, and billing make charging the first utility you can subscribe to.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              {[["Qi2", "certified in testing"], ["Ki", "certified in testing"], ["FCC", "certification in progress"], ["UL", "certification in progress"]].map(([m, s]) => (
                <div key={m} className="rounded-2xl border border-white/10 bg-white/5 px-6 py-4">
                  <p className="font-display text-2xl font-black text-white">{m}</p>
                  <p className="text-[11px] uppercase tracking-wider text-revo-ice/50">{s}</p>
                </div>
              ))}
              <div className="rounded-2xl border border-revo-green/40 bg-revo-green/10 px-6 py-4">
                <p className="font-display text-sm font-black text-revo-green">Founding member</p>
                <p className="text-[11px] uppercase tracking-wider text-revo-green/70">spots open</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden bg-revo-green px-5 py-28 md:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <h2 className="font-display text-4xl font-black leading-[1.02] tracking-tight text-navy sm:text-6xl">
              The last charger you&apos;ll ever think about.
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg font-medium text-navy/80">
              First 1,000 homes: professional installation free — plus first hardware allocation.
            </p>
            <Link to="/join" data-testid="closing-join-btn" className="mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-bold text-white transition-transform duration-200 hover:scale-105">
              Join the waitlist <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
